import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Star, Award, ExternalLink, RefreshCw, ChevronDown, ChevronUp, Briefcase, GraduationCap, Landmark, Cpu, Users, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import { AdminLayout } from '../components/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending_review: { bg: '#FFF8EC', color: '#D9A245', label: 'Pending Review' },
  active: { bg: '#DDF5F1', color: '#008C86', label: 'Active' },
  rejected: { bg: '#FEE2E2', color: '#DC2626', label: 'Rejected' },
  closed: { bg: '#F3F4F6', color: '#6B7280', label: 'Closed' },
  archived: { bg: '#F3F4F6', color: '#6B7280', label: 'Archived' },
};

const PLAN_LABELS = { basic: 'Basic', featured: 'Featured', premium: 'Premium', complimentary: 'Complimentary' };
const CATEGORY_ICONS = { healthcare: Stethoscope, education: GraduationCap, government: Landmark, business: Briefcase, technology: Cpu, nonprofit: Users };

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending_review;
  return <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 999, background: s.bg, color: s.color }}>{s.label}</span>;
}

function PlanBadge({ tier }) {
  const colors = { basic: '#6B7280', featured: '#008C86', premium: '#D9A245', complimentary: '#7C3AED' };
  return <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 999, background: 'rgba(0,0,0,.05)', color: colors[tier] || '#6B7280' }}>{PLAN_LABELS[tier] || tier}</span>;
}

function JobRow({ job, onApprove, onReject, onUpdate, token }) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState(job.admin_payment_note || '');
  const [payStatus, setPayStatus] = useState(job.payment_status || 'unpaid');
  const [isFeatured, setIsFeatured] = useState(job.is_featured);
  const [isHotlist, setIsHotlist] = useState(job.is_hotlist);
  const [isOPCurated, setIsOPCurated] = useState(job.is_op_curated);
  const [homepageFeatured, setHomepageFeatured] = useState(!!job.homepage_featured);
  const [useCustomImg, setUseCustomImg] = useState(!!job.use_custom_image);
  const [featuredImageUrl, setFeaturedImageUrl] = useState(job.featured_image_url || '');
  const CatIcon = CATEGORY_ICONS[job.category] || Briefcase;

  const saveNote = async () => {
    await onUpdate(job.id, {
      admin_payment_note: note,
      payment_status: payStatus,
      is_featured: isFeatured,
      is_hotlist: isHotlist,
      is_op_curated: isOPCurated,
      homepage_featured: homepageFeatured,
      use_custom_image: useCustomImg,
      featured_image_url: featuredImageUrl || null,
    });
    toast.success('Updated');
  };

  return (
    <div data-testid={`job-row-${job.id}`} style={{ background: '#fff', border: '1px solid rgba(221,212,197,.9)', borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
      {/* Row header */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, padding: '16px 20px', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--op-navy)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
            {job.logo_initial || job.organization?.slice(0, 3).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4, alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--op-navy)' }}>{job.title}</span>
              <StatusBadge status={job.status} />
              <PlanBadge tier={job.plan_tier} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--op-muted)' }}>{job.organization} · {job.location} · <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><CatIcon size={10} /> {job.category}</span></div>
            <div style={{ fontSize: 11, color: 'var(--op-muted)', marginTop: 4 }}>
              Payment: <strong>{job.payment_status || 'unpaid'}</strong>
              {job.payment_method_selected && ` via ${job.payment_method_selected}`}
              {job.invoice_number && ` · Inv: ${job.invoice_number}`}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          {job.status === 'pending_review' && (
            <>
              <button onClick={() => onApprove(job.id)} data-testid={`approve-btn-${job.id}`} style={{ background: 'var(--op-teal)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <CheckCircle2 size={13} /> Approve
              </button>
              <button onClick={() => onReject(job.id)} data-testid={`reject-btn-${job.id}`} style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FCA5A5', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <XCircle size={13} /> Reject
              </button>
            </>
          )}
          <button onClick={() => setExpanded(e => !e)} style={{ background: 'var(--op-cream)', border: 'none', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: 'var(--op-muted)' }}>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

          {/* Expanded detail panel */}
      {expanded && (
        <div style={{ borderTop: '1px solid rgba(221,212,197,.8)', padding: '16px 20px', background: 'var(--op-paper)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Left: curation flags + payment status + homepage placement */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--op-muted)', marginBottom: 10 }}>Curation Flags</div>
            {[
              { label: 'Featured', val: isFeatured, set: setIsFeatured },
              { label: 'Hotlist', val: isHotlist, set: setIsHotlist },
              { label: 'OP Curated', val: isOPCurated, set: setIsOPCurated },
            ].map(({ label, val, set }) => (
              <label key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={val} onChange={e => set(e.target.checked)} style={{ accentColor: 'var(--op-teal)', width: 14, height: 14 }} />
                {label}
              </label>
            ))}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--op-muted)', marginBottom: 8 }}>Payment Status</div>
              <select value={payStatus} onChange={e => setPayStatus(e.target.value)} style={{ width: '100%', border: '1.5px solid rgba(221,212,197,.9)', borderRadius: 8, padding: '8px 10px', fontSize: 12, background: '#fff' }}>
                {['unpaid', 'manual_pending', 'manual_paid', 'test_paid', 'complimentary', 'coupon_applied', 'rejected'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ marginTop: 12, borderTop: '1px solid rgba(221,212,197,.7)', paddingTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: '#008C86', marginBottom: 8 }}>Homepage Placement</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, cursor: 'pointer', color: homepageFeatured ? 'var(--op-teal)' : 'inherit', fontWeight: homepageFeatured ? 700 : 400 }}>
                <input
                  type="checkbox"
                  checked={homepageFeatured}
                  onChange={e => setHomepageFeatured(e.target.checked)}
                  style={{ accentColor: 'var(--op-teal)', width: 14, height: 14 }}
                  data-testid={`homepage-featured-toggle-${job.id}`}
                />
                Homepage Featured
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={useCustomImg} onChange={e => setUseCustomImg(e.target.checked)} style={{ accentColor: 'var(--op-teal)', width: 14, height: 14 }} />
                Use Custom Card Image
              </label>
              {useCustomImg && (
                <input
                  type="text"
                  value={featuredImageUrl}
                  onChange={e => setFeaturedImageUrl(e.target.value)}
                  placeholder="ImageKit URL for card image…"
                  style={{ width: '100%', border: '1.5px solid rgba(221,212,197,.9)', borderRadius: 8, padding: '7px 10px', fontSize: 12, background: '#fff', boxSizing: 'border-box', marginTop: 4 }}
                  data-testid={`featured-image-url-${job.id}`}
                />
              )}
            </div>
          </div>

          {/* Right: payment note + actions */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--op-muted)', marginBottom: 8 }}>Internal Payment Note</div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} style={{ width: '100%', border: '1.5px solid rgba(221,212,197,.9)', borderRadius: 8, padding: '8px 10px', fontSize: 12, resize: 'vertical', fontFamily: 'inherit', background: '#fff', boxSizing: 'border-box' }} placeholder="e.g. Check received 2026-01-15, amount $195…" />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button onClick={saveNote} style={{ background: 'var(--op-teal)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Save Changes</button>
              <Link to={`/career-hub/${job.id}`} target="_blank" style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--op-cream)', color: 'var(--op-muted)', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                <ExternalLink size={11} /> Preview
              </Link>
              {job.status === 'active' && (
                <button onClick={() => onReject(job.id)} style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FCA5A5', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Archive</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending_review');
  const [token, setToken] = useState(() => localStorage.getItem('auth_token') || '');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.set('status', filterStatus);
      const res = await fetch(`${API}/api/jb/admin/jobs?${params}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error('Auth required');
      const data = await res.json();
      setJobs(Array.isArray(data.jobs) ? data.jobs : []);
    } catch {
      toast.error('Could not load jobs — check login');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, token]);

  useEffect(() => { load(); }, [load]);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API}/api/jb/admin/jobs/${id}/approve`, { method: 'POST', headers: authHeaders });
      if (!res.ok) throw new Error();
      toast.success('Listing approved and published');
      load();
    } catch { toast.error('Approve failed — check auth'); }
  };

  const handleReject = async (id) => {
    try {
      const res = await fetch(`${API}/api/jb/admin/jobs/${id}/reject`, { method: 'POST', headers: authHeaders });
      if (!res.ok) throw new Error();
      toast.success('Listing rejected');
      load();
    } catch { toast.error('Reject failed — check auth'); }
  };

  const handleUpdate = async (id, updates) => {
    try {
      await fetch(`${API}/api/jb/admin/jobs/${id}`, { method: 'PATCH', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
      load();
    } catch { toast.error('Update failed'); }
  };

  const counts = jobs.reduce((acc, j) => { acc[j.status] = (acc[j.status] || 0) + 1; return acc; }, {});

  return (
    <AdminLayout title="Career Hub" breadcrumb="Job Review Queue">
    <div data-testid="admin-jobs-page" style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: "'Inter Tight', Inter, sans-serif", fontSize: 22, fontWeight: 900, color: 'var(--op-navy)', letterSpacing: '-.06em', margin: 0 }}>Career Hub — Admin Queue</h1>
          <p style={{ fontSize: 13, color: 'var(--op-muted)', margin: '4px 0 0' }}>Review, approve, and manage employer listings</p>
        </div>
        <button onClick={load} style={{ background: 'var(--op-cream)', border: 'none', borderRadius: 10, padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--op-muted)', fontWeight: 600 }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'Pending Review', key: 'pending_review', color: '#D9A245' },
            { label: 'Active', key: 'active', color: '#008C86' },
            { label: 'Rejected', key: 'rejected', color: '#DC2626' },
            { label: 'Total', key: 'all', color: 'var(--op-navy)' },
          ].map(({ label, key, color }) => (
            <div key={key} onClick={() => setFilterStatus(key)} style={{ background: filterStatus === key ? 'var(--op-navy)' : '#fff', border: `1px solid ${filterStatus === key ? 'var(--op-navy)' : 'rgba(221,212,197,.9)'}`, borderRadius: 12, padding: '14px 18px', cursor: 'pointer', transition: 'all .15s ease' }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: filterStatus === key ? '#fff' : color, letterSpacing: '-.04em' }}>{key === 'all' ? jobs.length : (counts[key] || 0)}</div>
              <div style={{ fontSize: 11, color: filterStatus === key ? 'rgba(255,255,255,.7)' : 'var(--op-muted)', fontWeight: 600 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Token input for testing */}
        {!token && (
          <div style={{ background: '#FFF8EC', border: '1px solid rgba(217,162,69,.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 12, color: '#92400E' }}>
            Admin token required for approve/reject. <Link to="/admin/login" style={{ color: 'var(--op-teal)', fontWeight: 700 }}>Log in</Link> or paste token:
            <input placeholder="Bearer token…" onChange={e => { setToken(e.target.value); localStorage.setItem('auth_token', e.target.value); }} style={{ marginLeft: 10, border: '1px solid rgba(217,162,69,.3)', borderRadius: 6, padding: '4px 8px', fontSize: 11 }} />
          </div>
        )}

        {/* Job list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--op-muted)' }}>
            <div style={{ width: 28, height: 28, border: '3px solid var(--op-teal-soft)', borderTopColor: 'var(--op-teal)', borderRadius: 999, animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
            Loading…
          </div>
        ) : jobs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--op-muted)' }}>
            <Briefcase size={36} style={{ marginBottom: 12, opacity: .4 }} />
            <p>No listings found for this filter.</p>
          </div>
        ) : (
          jobs.map(job => (
            <JobRow key={job.id} job={job} onApprove={handleApprove} onReject={handleReject} onUpdate={handleUpdate} token={token} />
          ))
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </AdminLayout>
  );
}
